import { useState, useEffect } from "react";
import { CreditCard, Plus, Search, Eye, Edit2, Trash2, Building2, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const API = process.env.REACT_APP_BACKEND_URL;

const methodLabels = {
  cash: "Cash",
  bank_transfer: "Bank Transfer",
  cheque: "Cheque",
  upi: "UPI",
  other: "Other"
};

export default function PaymentManagement() {
  const { token, user } = useAuth();
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [methodFilter, setMethodFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [customerInvoices, setCustomerInvoices] = useState([]);

  const [form, setForm] = useState({
    customer_id: "", invoice_id: "", amount: 0, payment_date: new Date().toISOString().split("T")[0],
    payment_method: "bank_transfer", reference_number: "", notes: ""
  });

  const canEdit = ["owner", "manager"].includes(user?.role);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [paymentsRes, customersRes, invoicesRes] = await Promise.all([
        fetch(`${API}/api/payments`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/customers`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API}/api/invoices`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      if (paymentsRes.ok) setPayments(await paymentsRes.json());
      if (customersRes.ok) setCustomers(await customersRes.json());
      if (invoicesRes.ok) setInvoices(await invoicesRes.json());
    } catch (e) { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  };

  const handleCustomerChange = (customerId) => {
    setForm({ ...form, customer_id: customerId, invoice_id: "" });
    const filtered = invoices.filter(inv => inv.customer_id === customerId && inv.status !== "paid");
    setCustomerInvoices(filtered);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Please select a customer"); return; }
    if (form.amount <= 0) { toast.error("Please enter a valid amount"); return; }

    const method = selectedPayment ? "PUT" : "POST";
    const url = selectedPayment ? `${API}/api/payments/${selectedPayment.id}` : `${API}/api/payments`;
    try {
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(selectedPayment ? "Payment updated" : "Payment recorded");
        setShowModal(false);
        resetForm();
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save payment");
      }
    } catch (e) { toast.error("Failed to save payment"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this payment? This will update the linked invoice balance.")) return;
    try {
      const res = await fetch(`${API}/api/payments/${id}`, {
        method: "DELETE", headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success("Payment deleted"); fetchData(); }
      else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete payment");
      }
    } catch (e) { toast.error("Failed to delete payment"); }
  };

  const openEdit = (payment) => {
    setSelectedPayment(payment);
    handleCustomerChange(payment.customer_id);
    setForm({
      customer_id: payment.customer_id,
      invoice_id: payment.invoice_id || "",
      amount: payment.amount,
      payment_date: payment.payment_date,
      payment_method: payment.payment_method,
      reference_number: payment.reference_number || "",
      notes: payment.notes || ""
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setSelectedPayment(null);
    setCustomerInvoices([]);
    setForm({
      customer_id: "", invoice_id: "", amount: 0, payment_date: new Date().toISOString().split("T")[0],
      payment_method: "bank_transfer", reference_number: "", notes: ""
    });
  };

  const filtered = payments.filter(p => {
    const matchesSearch = p.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
      p.reference_number?.toLowerCase().includes(search.toLowerCase());
    const matchesMethod = methodFilter === "all" || p.payment_method === methodFilter;
    return matchesSearch && matchesMethod;
  });

  const totalPayments = filtered.reduce((sum, p) => sum + p.amount, 0);

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-forest/20 border-t-forest rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="payment-management">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-serif font-bold text-navy">Payments</h1>
          <p className="text-slate-500 text-sm font-sans">Record and manage customer payments</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-forest hover:bg-forest/90" data-testid="add-payment-btn">
          <Plus className="w-4 h-4 mr-2" /> Record Payment
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-navy">₹{totalPayments.toLocaleString()}</p>
                <p className="text-sm text-slate-500">Total Payments ({filtered.length})</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search by customer or reference..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="search-payments" />
        </div>
        <Select value={methodFilter} onValueChange={setMethodFilter}>
          <SelectTrigger className="w-48" data-testid="method-filter">
            <SelectValue placeholder="All Methods" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
            <SelectItem value="cheque">Cheque</SelectItem>
            <SelectItem value="upi">UPI</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Reference</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(payment => {
              const linkedInvoice = invoices.find(inv => inv.id === payment.invoice_id);
              return (
                <TableRow key={payment.id} data-testid={`payment-row-${payment.id}`}>
                  <TableCell>{new Date(payment.payment_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      {payment.customer_name}
                    </div>
                  </TableCell>
                  <TableCell>
                    {linkedInvoice ? (
                      <Badge variant="outline" className="font-mono text-xs">{linkedInvoice.invoice_number}</Badge>
                    ) : (
                      <span className="text-slate-400 text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-bold text-green-600">₹{payment.amount.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge variant="secondary">{methodLabels[payment.payment_method]}</Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">{payment.reference_number || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canEdit && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => openEdit(payment)} data-testid={`edit-payment-${payment.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDelete(payment.id)} data-testid={`delete-payment-${payment.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {filtered.length === 0 && (
          <div className="py-12 text-center text-slate-500">
            {search || methodFilter !== "all" ? "No payments match your filters" : "No payments recorded yet"}
          </div>
        )}
      </Card>

      {/* Add/Edit Payment Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedPayment ? "Edit Payment" : "Record Payment"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Customer *</Label>
              <Select value={form.customer_id} onValueChange={handleCustomerChange} disabled={!!selectedPayment}>
                <SelectTrigger data-testid="select-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {form.customer_id && customerInvoices.length > 0 && (
              <div>
                <Label>Link to Invoice (Optional)</Label>
                <Select value={form.invoice_id} onValueChange={v => setForm({ ...form, invoice_id: v })}>
                  <SelectTrigger data-testid="select-invoice"><SelectValue placeholder="Select invoice (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">No linked invoice</SelectItem>
                    {customerInvoices.map(inv => (
                      <SelectItem key={inv.id} value={inv.id}>
                        {inv.invoice_number} - ₹{inv.grand_total?.toLocaleString()} ({inv.status})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label>Amount (₹) *</Label>
              <Input type="number" min="0.01" step="0.01" value={form.amount} onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} required data-testid="input-amount" />
            </div>

            <div>
              <Label>Payment Date *</Label>
              <Input type="date" value={form.payment_date} onChange={e => setForm({ ...form, payment_date: e.target.value })} required data-testid="input-date" />
            </div>

            <div>
              <Label>Payment Method *</Label>
              <Select value={form.payment_method} onValueChange={v => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="select-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Reference Number</Label>
              <Input value={form.reference_number} onChange={e => setForm({ ...form, reference_number: e.target.value })} placeholder="Transaction ID / Cheque No." data-testid="input-reference" />
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="input-notes" />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button type="submit" className="bg-forest hover:bg-forest/90" data-testid="submit-payment">{selectedPayment ? "Update" : "Record"} Payment</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
